import { collection, addDoc, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { ActivityActorType, ActivityLog, ActivityType } from '../types/typesActivityLog';
import { toEndOfDayIso, toStartOfDayIso } from '../utils/date';

function isFirestoreIndexError(error: unknown): boolean {
  const message = String((error as { message?: string })?.message || error || '');
  return message.includes('requires an index') || message.includes('FAILED_PRECONDITION');
}

export type ActivityLogUserOption = {
  userId: string;
  userName: string;
  userEmail?: string;
  actorType?: ActivityActorType;
  storeName?: string;
};

class ActivityLogService {
  private collectionName = 'activityLogs';

  /**
   * שמירת פעילות חדשה
   */
  async logActivity(params: {
    userId: string;
    userName: string;
    userEmail?: string;
    actorType?: ActivityActorType;
    actorRole?: string;
    storeName?: string;
    activityType: ActivityType;
    description: string;
    repairId?: string;
    repairCallNumber?: string;
    previousValue?: string;
    newValue?: string;
    metadata?: Record<string, any>;
  }): Promise<string> {
    try {
      const logEntry: Omit<ActivityLog, 'id'> & Record<string, any> = {
        userId: params.userId,
        userName: params.userName,
        activityType: params.activityType,
        description: params.description,
        actorType: params.actorType || 'employee',
        metadata: params.metadata || {},
        timestamp: new Date().toISOString(),
        createdAt: new Date().toISOString(),
      };

      if (params.userEmail !== undefined) {
        logEntry.userEmail = params.userEmail;
      }
      if (params.actorRole !== undefined && params.actorRole !== '') {
        logEntry.actorRole = params.actorRole;
      }
      if (params.storeName !== undefined && params.storeName !== '') {
        logEntry.storeName = params.storeName;
      }
      if (params.repairId !== undefined) {
        logEntry.repairId = params.repairId;
      }
      if (params.repairCallNumber !== undefined) {
        logEntry.repairCallNumber = params.repairCallNumber;
      }
      if (params.previousValue !== undefined) {
        logEntry.previousValue = params.previousValue;
      }
      if (params.newValue !== undefined) {
        logEntry.newValue = params.newValue;
      }

      const docRef = await addDoc(collection(db, this.collectionName), logEntry);
      return docRef.id;
    } catch (error) {
      const message = String((error as any)?.message || error || '');
      const isPermissionError =
        message.includes('Missing or insufficient permissions') ||
        message.toLowerCase().includes('permission');
      // לוג פעילות לא אמור לחסום זרימת עבודה (כמו שינוי סטטוס/שמירה)
      if (isPermissionError) {
        console.warn('Activity log skipped (no permission).');
        return '';
      }
      console.error('Error logging activity:', error);
      return '';
    }
  }

  /**
   * קריאת לוגים עם סינון
   */
  async getActivityLogs(filters?: {
    userId?: string;
    fromDate?: Date;
    toDate?: Date;
    activityType?: ActivityType;
    repairId?: string;
    limitCount?: number;
    /** פנימי — מונע לולאת fallback */
    _skipUserIdFallback?: boolean;
  }): Promise<ActivityLog[]> {
    const userIdFilter = String(filters?.userId || '').trim();

    try {
      return await this.fetchActivityLogsFromFirestore(filters, userIdFilter);
    } catch (error) {
      if (!filters?._skipUserIdFallback && userIdFilter && isFirestoreIndexError(error)) {
        console.warn(
          '[ActivityLogService] Firestore index missing for userId+timestamp — falling back to client-side user filter.',
        );
        const logs = await this.getActivityLogs({
          ...filters,
          userId: undefined,
          _skipUserIdFallback: true,
        });
        return logs.filter((log) => String(log.userId || '').trim() === userIdFilter);
      }
      console.error('Error fetching activity logs:', error);
      throw error;
    }
  }

  private async fetchActivityLogsFromFirestore(
    filters:
      | {
          userId?: string;
          fromDate?: Date;
          toDate?: Date;
          activityType?: ActivityType;
          repairId?: string;
          limitCount?: number;
        }
      | undefined,
    userIdFilter: string,
  ): Promise<ActivityLog[]> {
    let q = query(collection(db, this.collectionName));

    if (userIdFilter) {
      q = query(q, where('userId', '==', userIdFilter));
    }

    if (filters?.fromDate) {
      const fromIso = toStartOfDayIso(filters.fromDate);
      if (fromIso) q = query(q, where('timestamp', '>=', fromIso));
    }
    if (filters?.toDate) {
      const toIso = toEndOfDayIso(filters.toDate);
      if (toIso) q = query(q, where('timestamp', '<=', toIso));
    }

    if (filters?.activityType) {
      q = query(q, where('activityType', '==', filters.activityType));
    }

    if (filters?.repairId) {
      q = query(q, where('repairId', '==', filters.repairId));
    }

    q = query(q, orderBy('timestamp', 'desc'));

    if (filters?.limitCount) {
      q = query(q, limit(filters.limitCount));
    }

    const querySnapshot = await getDocs(q);
    const logs: ActivityLog[] = [];

    querySnapshot.forEach((docSnap) => {
      logs.push({
        id: docSnap.id,
        ...docSnap.data(),
      } as ActivityLog);
    });

    return logs;
  }

  /**
   * קבלת כל המשתמשים שביצעו פעולות
   */
  async getUniqueUsers(): Promise<ActivityLogUserOption[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        orderBy('timestamp', 'desc'),
        limit(1000), // נביא עד 1000 רשומות אחרונות כדי למצוא משתמשים
      );

      const querySnapshot = await getDocs(q);
      const usersMap = new Map<string, ActivityLogUserOption>();

      querySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.userId && !usersMap.has(data.userId)) {
          usersMap.set(data.userId, {
            userId: data.userId,
            userName: data.userName || '',
            userEmail: data.userEmail,
            actorType: data.actorType === 'external' ? 'external' : 'employee',
            storeName: data.storeName || undefined,
          });
        }
      });

      return Array.from(usersMap.values());
    } catch (error) {
      console.error('Error fetching unique users:', error);
      return [];
    }
  }
}

export const activityLogService = new ActivityLogService();
